/**
 * @file Equipe de chats : un chat par arme equipee (slot 0 = chef), placement en formation, chat aide IA (raid / coop asynchrone future).
 */
import type { EntityId } from '@shared/types';
import type { SimContext } from '../Context';
import type { Cat } from '../ecs/components';
import { spawnCat } from '../ecs/EntityFactory';
import type { With } from '../ecs/World';
import type { ResolvedWeapon } from '../weapons/WeaponStats';
import { resolveWeapon } from '../weapons/WeaponStats';

export class TeamService {
  private readonly helperCache = new Map<string, { key: string; value: ResolvedWeapon }>();

  constructor(private readonly ctx: SimContext) {}

  leader(): With<'cat'> | null {
    const id = this.ctx.rt.catIds[0];
    const e = id !== undefined ? this.ctx.rt.world.get(id) : undefined;
    return e && e.cat ? (e as With<'cat'>) : null;
  }

  cats(): Array<With<'cat'>> {
    const out: Array<With<'cat'>> = [];
    for (const id of this.ctx.rt.catIds) {
      const e = this.ctx.rt.world.get(id);
      if (e && e.cat && e.alive) out.push(e as With<'cat'>);
    }
    return out;
  }

  /** Reconstruit l'equipe : un chat par arme equipee (slot 0 toujours present), tir interdit => chef seul. */
  rebuild(): void {
    const ctx = this.ctx;
    const arena = ctx.modes.rules.arena;
    const old = this.leader();
    const x = old ? old.transform.x : 0;
    const y = old ? old.transform.y : -arena.halfH * 0.5;
    const rot = old ? old.transform.rot : Math.PI / 2;
    for (const id of ctx.rt.catIds) ctx.rt.world.destroy(id);
    ctx.rt.world.flush();
    this.helperCache.clear();

    const weapons = ctx.data.weapons;
    const unlocked = this.ctx.services.weapons.unlockedSlots();
    const canFire = ctx.modes.rules.control.canFire;
    const ids: EntityId[] = [];
    for (let slot = 0; slot < weapons.equipped.length; slot++) {
      const uid = weapons.equipped[slot] ?? null;
      if (slot > 0 && (!uid || slot >= unlocked || !canFire)) continue;
      const cat = spawnCat(ctx, slot, uid, x, y);
      cat.transform.rot = rot;
      ids.push(cat.id);
    }
    ctx.rt.catIds = ids;
    this.placeFormation(x, y);
    ctx.bus.emit('CatsRebuilt', { catIds: [...ids] });
  }

  /**
   * Cap de la formation (repere dans lequel formationOffset est exprime). Sur un chemin (arene 'corridor') c'est TOUJOURS
   * l'avant du chemin (+y) : les suiveurs restent derriere le chef quoi qu'il fasse (cible sur le cote, demi-tour du cap...),
   * sans balayage de la formation. Ailleurs : cap du chef.
   */
  formationHeading(leader: With<'cat'>): number {
    return this.ctx.modes.rules.arena.shape === 'corridor' ? Math.PI / 2 : leader.transform.rot;
  }

  /** Place le chef en (x, y) et les suiveurs selon formationOffset, sans interpolation parasite. */
  placeFormation(x: number, y: number): void {
    const cats = this.cats();
    const leader = cats[0];
    if (!leader) return;
    const rot = this.formationHeading(leader);
    const c = Math.cos(rot - Math.PI / 2);
    const s = Math.sin(rot - Math.PI / 2);
    cats.forEach((cat, i) => {
      const o = i === 0 ? { dx: 0, dy: 0 } : this.formationOffset(i);
      const px = x + o.dx * c - o.dy * s;
      const py = y + o.dx * s + o.dy * c;
      cat.transform.x = px;
      cat.transform.y = py;
      cat.transform.prevX = px;
      cat.transform.prevY = py;
    });
  }

  /**
   * Decalage de formation du suiveur i (1..n) par rapport au chef, dans le repere du chef (derriere lui, en V) :
   * i impair a gauche, i pair a droite, rang = ceil(i/2) ; espacement = combat.formationSpacing.
   */
  formationOffset(i: number): { dx: number; dy: number } {
    const s = this.ctx.config.balance.combat.formationSpacing;
    const rank = Math.ceil(i / 2);
    const side = i % 2 === 1 ? -1 : 1;
    return { dx: side * s * rank * 0.9, dy: -s * rank * 0.8 };
  }

  /** Ajoute un chat aide IA (arme : meilleure arme non equipee, sinon copie de celle du chef). */
  addHelper(): EntityId | null {
    const ctx = this.ctx;
    const leader = this.leader();
    if (!leader) return null;
    const w = ctx.data.weapons;
    const free = w.items.filter((i) => !w.equipped.includes(i.uid));
    const dps = (defId: string): number => {
      const d = ctx.index.weapon(defId);
      return d.baseDamage / d.interval;
    };
    const best = [...free].sort((a, b) => dps(b.defId) - dps(a.defId) || a.obtainedSeq - b.obtainedSeq)[0];
    const uid = best?.uid ?? leader.cat.weaponUid;
    if (!uid) return null;
    const cat = spawnCat(ctx, -1, uid, leader.transform.x, leader.transform.y);
    cat.cat.isHelper = true;
    cat.cat.isLeader = false;
    cat.cat.aiState = 'assist';
    ctx.rt.catIds.push(cat.id);
    this.placeFormation(leader.transform.x, leader.transform.y);
    ctx.bus.emit('CatsRebuilt', { catIds: [...ctx.rt.catIds] });
    return cat.id;
  }

  /** Arme resolue d'un chat (slot equipe, ou arme du chat aide mise en cache par version des stats). */
  resolveFor(cat: Cat): ResolvedWeapon | null {
    const ctx = this.ctx;
    if (cat.slot >= 0) return ctx.weaponStats.forSlot(ctx.data, cat.slot, ctx.modes.currentId);
    if (!cat.weaponUid) return null;
    ctx.stats.get('damage');
    const key = `${ctx.stats.version}|${cat.weaponUid}|${ctx.modes.currentId}`;
    const hit = this.helperCache.get(cat.weaponUid);
    if (hit && hit.key === key) return hit.value;
    const item = ctx.data.weapons.items.find((i) => i.uid === cat.weaponUid);
    if (!item) return null;
    const value = resolveWeapon(ctx.config, ctx.index.weapon(item.defId), item.uid, 0, ctx.stats, ctx.modes.currentId);
    this.helperCache.set(cat.weaponUid, { key, value });
    return value;
  }
}
